import { shallowRef } from 'vue'
import type { RouteLocationNormalized } from 'vue-router'
import { createRouter, createWebHistory, START_LOCATION } from 'vue-router'

import { isAuthenticated } from '@/libs/auth'
import { useAppStore } from '@/stores/app'

/**
 * The route name „Danes“ is registered under, spelled here and imported from here.
 *
 * It lives in the router rather than beside the RULE (`libs/landing.ts`) because of the
 * direction of the imports: that module reads `libs/nav.ts`, which reads this one for the
 * router it filters the menu against. Taking the name from there would close the ring at
 * load time and leave whichever module the bundler evaluated first looking at a half-built
 * one — an empty menu or an undefined router, in a built bundle only and never in
 * development. The name travels this way; the rule is asked the other way, dynamically,
 * at a moment when everything is built.
 */
export const LANDING_ROUTE = 'today'

/**
 * The landing rule, imported at the moment it is asked rather than at module load — see
 * the note above. Both callers already await, so it costs nothing.
 */
const landingRule = () => import('@/libs/landing')

const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    scrollBehavior(to, from, savedPosition) {
        if (savedPosition)         return savedPosition
        if (to.hash)               return { el: to.hash }
        if (from.path === to.path) return {}

        return { top: 0, behavior: 'smooth' }
    },
    routes: [
        {
            // Where a person goes when they have not asked for anywhere in particular.
            //
            // „Danes“ for everybody, except an account whose whole working menu is one
            // screen — a housekeeper, a shop assistant — who lands on that screen. The
            // rule is `libs/landing.ts` and is shared with the sign-in, because two
            // copies of it would send somebody to a different place depending on
            // whether they signed in or reloaded.
            //
            // It answers „Danes“ flatly, because a redirect cannot await and at this
            // moment the account has not been read: asking the rule here would put the
            // question while the menu is still empty, and an empty menu is not an account
            // with no rights. The guard below asks it properly, once the licence and the
            // permissions are in.
            path:     '/',
            redirect: () => ({ name: LANDING_ROUTE }),
        },
        {
            // „Danes“ — what is happening in this house today and what waits for this
            // account, assembled from the screens they may open (docs/DANES.md).
            //
            // No `meta.permission`: the page is drawn from what the service answers, and
            // an account that holds nothing it can speak about is told so in one card
            // rather than sent somewhere else.
            path:      '/danes',
            name:      'today',
            component: () => import('@/views/today/Today.vue'),
            meta:      { requiresAuth: true },
        },
        {
            // The telephone's page. Public and outside everything: it is opened from a
            // code on the desk's screen by somebody who will not sign in on a phone,
            // and the token in the address is the whole of its authority.
            path:      '/scan/:token',
            name:      'document-scan',
            component: () => import('@/views/scan/DocumentScan.vue'),
            meta:      { layout: 'Blank', requiresAuth: false },
        },
        {
            // The one screen a module this hotel has not bought leads to
            // (docs/MODULI.md §3.3). One page and not a per-module one: what it has to
            // say is the same sentence every time, and the module's own name is a
            // query away.
            path:      '/modul',
            name:      'module-off',
            component: () => import('@/views/ModuleOff.vue'),
            meta:      { requiresAuth: true },
        },
        {
            path:      '/login',
            name:      'login',
            component: () => import('@/views/Login.vue'),
            meta:      { layout: 'Blank', requiresAuth: false },
        },
        {
            // The presentation server's entry page and its one-click sign-in (Logic/Demo.go).
            // Both open to anybody, and both do nothing on a hotel's deployment: the service
            // answers 404 wherever it has no demo accounts, and the page then says so.
            path:      '/demo',
            name:      'demo',
            component: () => import('@/views/demo/Demo.vue'),
            meta:      { layout: 'Document', requiresAuth: false },
        },
        {
            path:      '/demo/:role',
            name:      'demo-sign-in',
            component: () => import('@/views/demo/DemoSignIn.vue'),
            meta:      { layout: 'Blank', requiresAuth: false },
        },
        {
            // The door the group console opens this portal through (docs/UPRAVA.md §9,
            // WP-G0). It is `/marketing/open`'s counterpart read backwards: there,
            // reception opens marketing; here, the head office opens reception.
            //
            // `requiresAuth: false` and that is the point — the person arriving has no
            // session in THIS portal yet. What they carry is a fifteen-minute handoff in
            // the address fragment, which the view swaps for an ordinary session before it
            // navigates anywhere. A guard that demanded a session here would refuse the one
            // page whose whole job is to get one.
            //
            // `layout: 'Blank'` for the same reason the sign-in has it: there is no menu to
            // draw until the account has been read, and a shell drawn around a page that is
            // about to redirect is a flash of somebody else's hotel.
            path:      '/handoff',
            name:      'handoff',
            component: () => import('@/views/Handoff.vue'),
            meta:      { layout: 'Blank', requiresAuth: false },
        },
        {
            path:      '/chessboard',
            name:      'chessboard',
            component: () => import('@/views/chessboard/Chessboard.vue'),
            meta:      { requiresAuth: true },
        },
        {
            // What is left to sell, by room type and by day.
            //
            // It used to be a band across the top of the board, where it was read while
            // scrolling past sixty rooms — and where the one thing a desk wants to do
            // with a free count, which is to sell one of them, could not be done at all.
            // Its own screen, and pressing a number opens the rooms behind it.
            path:      '/availability',
            name:      'availability',
            component: () => import('@/views/availability/Availability.vue'),
            meta:      { requiresAuth: true },
        },
        {
            // One room, whole: what it is doing, what may be told to it, who has it
            // tonight and everything that has happened in it. Opened from a chip in the
            // board's live column — which is the journey it exists for.
            path:      '/rooms/:roomId',
            name:      'room-screen',
            component: () => import('@/views/room/RoomScreen.vue'),
            meta:      { requiresAuth: true },
        },
        {
            path:      '/reservations',
            name:      'reservations',
            component: () => import('@/views/reservation/Reservations.vue'),
            meta:      { requiresAuth: true },
        },
        {
            path:      '/reservations/:id',
            name:      'reservation',
            component: () => import('@/views/reservation/Reservation.vue'),
            meta:      { requiresAuth: true },
        },
        {
            // Opomniki: every open reminder of the house, across its bookings, in the
            // three groups a desk reads them in. A page of its own beside the bookings
            // rather than a panel inside one, because the question it answers — "what
            // still has to be done today" — is not about any single reservation.
            path:      '/opomniki',
            name:      'reminders',
            component: () => import('@/views/reminder/Reminders.vue'),
            meta:      { requiresAuth: true },
        },
        {
            // The guest, whole: the CRM's 360 view — numbers, timeline, stays,
            // preferences and the hotel's own notes.
            path:      '/crm/guests/:id',
            name:      'crm-guest',
            component: () => import('@/views/crm/GuestProfile.vue'),
            meta:      { requiresAuth: true },
        },
        {
            // Obroki in vstopi — the morning at the dining room door, as reception
            // reads it (docs/DINING.md section 7).
            path:      '/obroki',
            name:      'dining',
            component: () => import('@/views/dining/Dining.vue'),
            meta:      { requiresAuth: true },
        },
        {
            path:      '/skupine',
            name:      'groups',
            component: () => import('@/views/group/Groups.vue'),
            meta:      { requiresAuth: true, module: 'groups_events' },
        },
        {
            /**
             * ONE group, on the same screen as the list.
             *
             * The same component and not a second one, because a group's page IS its row
             * opened: the rooming, the money and the ladder are all already there, and a
             * separate screen would be a second place to keep them in step.
             *
             * It exists because a block on the chessboard had nowhere to go. Pressing a
             * held room used to open the LIST — the desk was taken off the board, given
             * thirteen groups and left to find the one it had just pointed at, which is
             * the opposite of what pressing a reservation does.
             */
            path:      '/skupine/:id',
            name:      'group',
            component: () => import('@/views/group/Groups.vue'),
            meta:      { requiresAuth: true, module: 'groups_events' },
        },
        {
            /**
             * The event manager (docs/DOGODKI.md §3.3). Slovenian, like /skupine and
             * /obroki, and under the SAME licence key: MICE is one story, and a house
             * that has bought the halls has bought the events in them.
             */
            path:      '/dogodki',
            name:      'events',
            component: () => import('@/views/event/Events.vue'),
            meta:      { requiresAuth: true, module: 'groups_events' },
        },
        {
            /**
             * The halls' diary. BEFORE /dogodki/:id deliberately — a static segment wins
             * over a parameter either way, but the pair reads as a pair here.
             */
            path:      '/dogodki/koledar',
            name:      'events-calendar',
            component: () => import('@/views/event/EventCalendar.vue'),
            meta:      { requiresAuth: true, module: 'groups_events' },
        },
        {
            /**
             * The sheet a department works an evening from, on its own page rather than
             * in a dialog: it is read on a telephone in a kitchen and it is printed
             * (docs/DOGODKI.md §3.3). Before /dogodki/:id for the calendar's reason.
             */
            path:      '/dogodki/:id/beo',
            name:      'event-beo',
            component: () => import('@/views/event/EventBEO.vue'),
            meta:      { requiresAuth: true, module: 'groups_events' },
        },
        {
            path:      '/dogodki/:id',
            name:      'event',
            component: () => import('@/views/event/Event.vue'),
            meta:      { requiresAuth: true, module: 'groups_events' },
        },
        {
            path:      '/pomocnik',
            name:      'assistant',
            component: () => import('@/views/assistant/Assistant.vue'),
            meta:      { requiresAuth: true },
        },
        {
            path:      '/crm/vodstvo',
            name:      'crm-manager',
            component: () => import('@/views/crm/Manager.vue'),
            meta:      { requiresAuth: true },
        },
        {
            path:      '/crm/pregled',
            name:      'crm-dashboard',
            component: () => import('@/views/crm/Dashboard.vue'),
            meta:      { requiresAuth: true },
        },
        {
            path:      '/crm/companies/:id',
            name:      'crm-company',
            component: () => import('@/views/crm/CompanyProfile.vue'),
            meta:      { requiresAuth: true },
        },
        {
            path:      '/crm/opportunities',
            name:      'crm-opportunities',
            component: () => import('@/views/crm/Opportunities.vue'),
            meta:      { requiresAuth: true },
        },
        {
            path:      '/crm/segments',
            name:      'crm-segments',
            component: () => import('@/views/crm/Segments.vue'),
            meta:      { requiresAuth: true },
        },
        {
            path:      '/crm/tasks',
            name:      'crm-tasks',
            component: () => import('@/views/crm/Tasks.vue'),
            meta:      { requiresAuth: true },
        },
        {
            path:      '/guests',
            name:      'guests',
            component: () => import('@/views/guest/Guests.vue'),
            meta:      { requiresAuth: true },
        },
        {
            // The register of what guests leave behind. The desk's side of it: the
            // housekeeper writes the find down on her own portal.
            path:      '/lost-items',
            name:      'lost-items',
            component: () => import('@/views/lost/LostItems.vue'),
            meta:      { requiresAuth: true, module: 'operations' },
        },
        {
            // The cars the desk arranges for guests: the airport pickup, the run to the
            // station. Its own page rather than a panel on a booking, because half of
            // them belong to no booking at all — a day visitor, a supplier — and because
            // the question a morning shift asks is "what is going out today", which is a
            // list of the house's rides and not of one guest's.
            //
            // Opened from a reservation as ?reservation=<id>&open=1, which narrows the
            // register to that stay and brings the dialog up against it.
            path:      '/transfers',
            name:      'transfers',
            component: () => import('@/views/transfer/Transfers.vue'),
            meta:      { requiresAuth: true },
        },
        {
            path:      '/properties',
            name:      'properties',
            component: () => import('@/views/property/Properties.vue'),
            meta:      { requiresAuth: true },
        },
        {
            path:      '/properties/:id',
            name:      'property',
            component: () => import('@/views/property/Property.vue'),
            meta:      { requiresAuth: true },
        },
        {
            path:      '/rate-plans',
            name:      'rate-plans',
            component: () => import('@/views/rate/RatePlans.vue'),
            meta:      { requiresAuth: true },
        },
        {
            path:      '/companies',
            name:      'companies',
            component: () => import('@/views/company/Companies.vue'),
            meta:      { requiresAuth: true },
        },
        {
            path:      '/channels',
            name:      'channels',
            component: () => import('@/views/channel/Channels.vue'),
            meta:      { requiresAuth: true, module: 'distribution' },
        },
        {
            // A folio is reached from its reservation, never from the navigation: it
            // belongs to a stay, and a list of everybody's open cards is not a question
            // a receptionist asks.
            path:      '/folios/:id',
            name:      'folio',
            component: () => import('@/views/folio/Folio.vue'),
            meta:      { requiresAuth: true },
        },
        {
            path:      '/maintenance',
            name:      'maintenance',
            component: () => import('@/views/maintenance/Maintenance.vue'),
            meta:      { requiresAuth: true, module: 'operations' },
        },
        {
            // „Čiščenje — pregled“: the housekeeping morning read from the desk
            // (docs/HOUSEKEEPING.md „Pregled za recepcijo in vodstvo“). Read-only —
            // the round is arranged in the housekeeping application, on a telephone.
            //
            // `operations` and not a housekeeping key of its own: housekeeping and
            // maintenance are one module and one licence (docs/MODULI.md §1), and the
            // API gates every route of this domain on exactly that. No permission on
            // the route, like the rest of this portal: the API guards the three reads
            // behind it, the menu entry carries `housekeeping:read`, and a key spelled
            // here as well would be a second place to correct when it changes.
            path:      '/housekeeping/overview',
            name:      'housekeeping-overview',
            component: () => import('@/views/housekeeping/Overview.vue'),
            meta:      { requiresAuth: true, module: 'operations' },
        },
        {
            path:      '/facilities',
            name:      'facilities',
            component: () => import('@/views/facility/Facilities.vue'),
            meta:      { requiresAuth: true, module: 'groups_events' },
        },
        {
            // The list moved into Finance as its Računi tab; the old address still
            // answers, so bookmarks and habits keep working.
            path:     '/invoices',
            name:     'invoices',
            redirect: { name: 'finance', query: { tab: 'invoices' } },
        },
        {
            // The document itself, on a page with no navigation around it: what a guest
            // is handed and an accountant files. The Document layout is what makes it
            // print as a document rather than as a screen with a menu down the side —
            // and, unlike Blank, it does not cap the page at the login card's width.
            path:      '/invoices/:id/print',
            name:      'invoice-print',
            component: () => import('@/views/invoice/InvoicePrint.vue'),
            meta:      { requiresAuth: true, layout: 'Document' },
        },
        {
            path:      '/invoices/:id',
            name:      'invoice',
            component: () => import('@/views/invoice/Invoice.vue'),
            meta:      { requiresAuth: true },
        },
        // The three screens of Imprion Materials (docs/MATERIALNO.md §10). All three
        // carry `meta.module`, so a bookmark kept from before the licence was written
        // lands on „modul ni vključen“ rather than on a screen whose every read is
        // refused — and the routes behind them are gated on the server as well
        // (HTTP_API/Stock.go, Retail.go).
        {
            // „Kaj me čaka“ — the module's front door (docs/MATERIALNO.md §10.11).
            //
            // `/materials` and not `/materials/overview`: this is what the whole
            // branch is for, and every other screen of the module already hangs a
            // segment off that word. It carries `meta.module` like its siblings, so a
            // bookmark kept from before the licence lands on „modul ni vključen“
            // rather than on a screen whose one read is refused.
            //
            // No permission on the route, like `staff-today` and for the same reason:
            // the API guards the read with five keys and narrows the panels to the
            // ones the reader holds, and a key here would be a sixth spelling of a
            // rule the server owns.
            path:      '/materials',
            name:      'materials-overview',
            component: () => import('@/views/materials/Overview.vue'),
            meta:      { requiresAuth: true, module: 'materials' },
        },
        {
            path:      '/suppliers',
            name:      'suppliers',
            component: () => import('@/views/stock/Suppliers.vue'),
            meta:      { requiresAuth: true, module: 'materials' },
        },
        {
            path:      '/stock',
            name:      'stock',
            component: () => import('@/views/stock/Stock.vue'),
            meta:      { requiresAuth: true, module: 'materials' },
        },
        {
            // The accounting half of the same module
            // (docs/MATERIALNO-KNJIGOVODSTVO.md, WP-M2b): the cost centres, the chart
            // of accounts, the mapping and the hand-off to the book-keeper. It carries
            // `meta.module` like its three siblings, so a bookmark kept from before
            // the licence lands on „modul ni vključen“ rather than on a screen whose
            // every read is refused.
            path:      '/materials/accounting',
            name:      'materials-accounting',
            component: () => import('@/views/materials/accounting/Accounting.vue'),
            meta:      { requiresAuth: true, module: 'materials' },
        },
        {
            // The shop counter, under Materials because what it sells is what the
            // store keeps: a till is bound to a store room (docs/MATERIALNO.md § 7),
            // which is also why it is inside this module's licence and not its own.
            //
            // It carries permissions for the reason the housekeeping portal's routes do:
            // there is no readable half of this screen. `retail:sell` is the keypad, the
            // shift and the drawer, and an account without it reaches a page that can do
            // nothing but say so — from a bookmark, because the menu does not draw the
            // entry (`layouts/components/Sidebar.vue`).
            //
            // All THREE keys, because that is what the screen's first reads ask for:
            // `/retail/tills` is `retail:read`, `/stock/items`, `/stock/places` and
            // `/stock/balances` are `stock:read`, and `/retail/rooms` and the shift are
            // `retail:sell`. On `retail:sell` alone the till opened and then drew
            // nothing it could sell. Reading the tills on their own is Settings →
            // Blagajne, which is a different screen and keeps `retail:read` alone.
            path:      '/retail',
            name:      'retail',
            component: () => import('@/views/retail/Retail.vue'),
            meta:      { requiresAuth: true, module: 'materials', permissions: ['retail:sell', 'retail:read', 'stock:read'] },
        },
        {
            // The BUYING half of Materials (docs/MATERIALNO-KATALOG.md, WP-M3a): the
            // supplier's catalogue on one tab and the contracts that write its prices
            // on the other. It carries `meta.module` like every sibling above, so a
            // bookmark kept from before the licence lands on „modul ni vključen“ rather
            // than on a screen whose every read is refused.
            path:      '/materials/catalogue',
            name:      'materials-catalogue',
            component: () => import('@/views/materials/catalogue/Catalogue.vue'),
            meta:      { requiresAuth: true, module: 'materials' },
        },
        {
            // The accounting flow of Materials (docs/MATERIALNO-RACUNI.md, WP-M2): the
            // supplier's invoices, the three-way match and the credit note. Both carry
            // `meta.module`, like the screens above, so a bookmark kept from before the
            // licence lands on „modul ni vključen“ rather than on a screen whose every
            // read is refused.
            //
            // The register asks for `stock:read` beside `materials:invoice`, because the
            // supplier list is the first read the list makes and every row's name comes
            // out of it. The screen tolerates a refusal now (blank names rather than an
            // empty register), but an account that cannot read a supplier at all is
            // better sent home than shown a list of invoices with no names on it.
            path:      '/materials/invoices',
            name:      'materials-invoices',
            component: () => import('@/views/materials/invoices/Invoices.vue'),
            meta:      { requiresAuth: true, module: 'materials', permissions: ['materials:invoice', 'stock:read'] },
        },
        {
            path:      '/materials/invoices/:id',
            name:      'materials-invoice',
            component: () => import('@/views/materials/invoices/Invoice.vue'),
            meta:      { requiresAuth: true, module: 'materials' },
        },

        // Prevzem: karantena in vračila dobavitelju (docs/MATERIALNO-PREVZEM.md, WP-M3c).
        {
            path:      '/materials/receiving',
            name:      'materials-receiving-issues',
            component: () => import('@/views/materials/receiving/Receiving.vue'),
            meta:      { requiresAuth: true, module: 'materials' },
        },
        {
            path:      '/materials/returns',
            name:      'materials-returns',
            component: () => import('@/views/materials/receiving/Returns.vue'),
            meta:      { requiresAuth: true, module: 'materials' },
        },
        {
            path:      '/materials/returns/:id',
            name:      'materials-return',
            component: () => import('@/views/materials/receiving/Return.vue'),
            meta:      { requiresAuth: true, module: 'materials' },
        },

        // Mobilno materialno (docs/MATERIALNO-MOBILNO.md, WP-M7): the store room's own
        // three flows, drawn for a telephone. Four routes and not one, because a person
        // in a yard bookmarks the flow they walk every morning rather than a menu — and
        // because a refresh in the middle of a delivery must come back to the delivery.
        // All four carry `meta.module` like every sibling above, so a bookmark kept from
        // before the licence lands on „modul ni vključen“ rather than on a screen whose
        // every read is refused.
        {
            path:      '/materials/mobile',
            name:      'materials-mobile',
            component: () => import('@/views/materials/mobile/Mobile.vue'),
            meta:      { requiresAuth: true, module: 'materials' },
        },
        {
            path:      '/materials/mobile/receive',
            name:      'materials-mobile-receive',
            component: () => import('@/views/materials/mobile/Receive.vue'),
            meta:      { requiresAuth: true, module: 'materials' },
        },
        {
            path:      '/materials/mobile/count',
            name:      'materials-mobile-count',
            component: () => import('@/views/materials/mobile/Count.vue'),
            meta:      { requiresAuth: true, module: 'materials' },
        },
        {
            path:      '/materials/mobile/issue',
            name:      'materials-mobile-issue',
            component: () => import('@/views/materials/mobile/Issue.vue'),
            meta:      { requiresAuth: true, module: 'materials' },
        },
        {
            // The supervisor's half of the stocktake — naming the rounds, the review
            // table and the posting. Its own route rather than a tab of the mobile hub,
            // because it is read sitting down beside whoever has to explain the
            // differences.
            path:      '/materials/counts',
            name:      'materials-counts',
            component: () => import('@/views/materials/counts/Counts.vue'),
            meta:      { requiresAuth: true, module: 'materials' },
        },
        {
            // The BUYING half of the same module (docs/MATERIALNO-NAROCILA.md, WP-M3b):
            // the purchase order's own life, and the request for quotation to several
            // suppliers that precedes it. All four carry `meta.module` like their
            // siblings above, so a bookmark kept from before the licence lands on
            // „modul ni vključen“ rather than on a screen whose every read is refused.
            path:      '/materials/orders',
            name:      'materials-orders',
            component: () => import('@/views/materials/orders/Orders.vue'),
            meta:      { requiresAuth: true, module: 'materials' },
        },
        {
            path:      '/materials/orders/:id',
            name:      'materials-order',
            component: () => import('@/views/materials/orders/Order.vue'),
            meta:      { requiresAuth: true, module: 'materials' },
        },
        {
            path:      '/materials/rfqs',
            name:      'materials-rfqs',
            component: () => import('@/views/materials/orders/RFQs.vue'),
            meta:      { requiresAuth: true, module: 'materials' },
        },
        {
            path:      '/materials/rfqs/:id',
            name:      'materials-rfq',
            component: () => import('@/views/materials/orders/RFQ.vue'),
            meta:      { requiresAuth: true, module: 'materials' },
        },
        {
            // The ASKING half of the same module (docs/MATERIALNO-ZAHTEVKI.md, WP-M4):
            // the requisition a department writes, the ladder it has to climb, and the
            // rules that decide how many signatures that is. All four carry
            // `meta.module` like their siblings above, so a bookmark kept from before
            // the licence lands on „modul ni vključen“ rather than on a screen whose
            // every read is refused.
            path:      '/materials/requisitions',
            name:      'materials-requisitions',
            component: () => import('@/views/materials/requisitions/Requisitions.vue'),
            meta:      { requiresAuth: true, module: 'materials' },
        },
        {
            path:      '/materials/requisitions/:id',
            name:      'materials-requisition',
            component: () => import('@/views/materials/requisitions/Requisition.vue'),
            meta:      { requiresAuth: true, module: 'materials' },
        },
        {
            path:      '/materials/approvals',
            name:      'materials-approvals',
            component: () => import('@/views/materials/approvals/Approvals.vue'),
            meta:      { requiresAuth: true, module: 'materials' },
        },
        {
            path:      '/materials/approval-rules',
            name:      'materials-approval-rules',
            component: () => import('@/views/materials/approvals/ApprovalRules.vue'),
            meta:      { requiresAuth: true, module: 'materials' },
        },
        {
            // The PLANNING half of the same module (docs/MATERIALNO-NAPOVED-ZASLONI.md,
            // WP-M6): what the days ahead will consume, out of the hotel's own arrivals,
            // nights, cleanings, breakfasts and events — and the profiles that say how
            // much of what. Both carry `meta.module` like their siblings above, so a
            // bookmark kept from before the licence lands on „modul ni vključen“ rather
            // than on a screen whose every read is refused.
            path:      '/materials/forecast',
            name:      'materials-forecast',
            component: () => import('@/views/materials/forecast/Forecast.vue'),
            meta:      { requiresAuth: true, module: 'materials' },
        },
        {
            // Proračuni, the other half of WP-M6. It sits beside the forecast rather
            // than inside Knjigovodstvo because the two answer one question between
            // them — what the house will need, and what it meant to spend on it — but
            // the RIGHT is the accounting one, on the route and on the server alike.
            path:      '/materials/budgets',
            name:      'materials-budgets',
            component: () => import('@/views/materials/budgets/Budgets.vue'),
            meta:      { requiresAuth: true, module: 'materials' },
        },
        {
            // Kazalniki (docs/MATERIALNO-KAZALNIKI.md): the six management indicators
            // of a period. Under /materials/reports/ rather than beside the screens
            // above, because it READS every one of them and writes nothing — the same
            // separation /reports has from the screens it is drawn from.
            //
            // `meta.module` like its siblings, and no permission in the meta: the two
            // rights that open it are checked on the server and in the sidebar's own
            // allow(), and a route that carried a third copy would be a third place
            // for them to drift.
            path:      '/materials/reports/kpi',
            name:      'materials-kpi',
            component: () => import('@/views/materials/reports/KPI.vue'),
            meta:      { requiresAuth: true, module: 'materials' },
        },
        {
            // Mesečni zaključek (docs/MATERIALNO-ZAKLJUCEK.md): the checklist and the
            // button that shuts a month. Beside the indicators because they are the two
            // halves of one morning — how the month went, and whether it is finished.
            path:      '/materials/reports/period-close',
            name:      'materials-period-close',
            component: () => import('@/views/materials/reports/PeriodClose.vue'),
            meta:      { requiresAuth: true, module: 'materials' },
        },
        {
            // Veriga (WP-M5, docs/VERIGE-ZASLONI.md): the chain's own catalogue and
            // contracts, the demand several houses add up to, and who the houses are.
            // `meta.module: 'chains'` and NOT 'materials' — the licence being asked
            // about here is the chain one, and a house that bought Materials without it
            // must land on „modul ni vključen“ rather than on a screen whose every read
            // is refused. The house being in no chain is a different answer and is given
            // by the screen itself.
            //
            // `chains:read` beside `materials:procure`: the screen's FIRST call is
            // `GET /properties/{id}/chain/`, which is the chain key, and everything the
            // tabs then buy is the buyer's. On `materials:procure` alone the page opened
            // and drew „ta hiša ni v verigi“ over a refusal, which is a house being told
            // it has no chain because it may not read one.
            path:      '/materials/chain',
            name:      'materials-chain',
            component: () => import('@/views/materials/chain/Chain.vue'),
            meta:      { requiresAuth: true, module: 'chains', permissions: ['chains:read', 'materials:procure'] },
        },
        {
            // Prenosi med objekti verige, and the one pair of screens in the portal
            // that stands on TWO licences: `materials`, because stock that moves between
            // two houses is stock and a house without the module has no shelf for it to
            // leave, and `chains`, because without one there is nowhere for it to go.
            // Both are named, so a house holding either alone lands on „modul ni
            // vključen“ naming the one it is missing.
            path:      '/materials/transfers',
            name:      'materials-transfers',
            component: () => import('@/views/materials/transfers/Transfers.vue'),
            meta:      { requiresAuth: true, modules: ['materials', 'chains'] },
        },
        {
            path:      '/materials/transfers/:id',
            name:      'materials-transfer',
            component: () => import('@/views/materials/transfers/Transfer.vue'),
            meta:      { requiresAuth: true, modules: ['materials', 'chains'] },
        },
        {
            path:      '/offers',
            name:      'offers',
            component: () => import('@/views/offer/Offers.vue'),
            meta:      { requiresAuth: true },
        },
        {
            // Poročanje AJPES — what the house has told the state about its guests, and
            // what the state is still owed today (docs/ETURIZEM.md).
            //
            // It was the fifth tab of `/reports` until 14. 9. 2026. A filing has twelve
            // hours (docs/ETURIZEM.md §2), so the screen belongs to the desk that can meet
            // that deadline rather than to the reports somebody opens at the end of the
            // month — Davor: „je zelo pomemben podatek za recepcionarja“.
            //
            // `compliance:read` on the route as well as on the menu entry, because it is
            // the key every one of this screen's reads is guarded by
            // (HTTP_API/Compliance.go). No module: guest reporting is the law and not a
            // thing a house buys.
            path:      '/registrations',
            name:      'registrations',
            component: () => import('@/views/registration/Registrations.vue'),
            meta:      { requiresAuth: true, permission: 'compliance:read' },
        },
        {
            path:      '/reports',
            name:      'reports',
            component: () => import('@/views/report/Reports.vue'),
            meta:      { requiresAuth: true, module: 'reports' },
            /**
             * The old address of Poročanje AJPES, which was `?tab=registrations` here.
             *
             * A guard rather than a route of its own, because the thing that moved is a
             * QUERY on this path and not a path: vue-router matches on the path, so there
             * is no second route to declare. The query is dropped on the way — it named a
             * tab that no longer exists — and anything else about the address is left
             * alone, so `/reports?tab=occupancy` is untouched.
             *
             * It is kept for the reason `/staff/my-rota` is: links have been bookmarked
             * and sent, and a favourite that lands on the wrong tab is a screen somebody
             * concludes has been deleted.
             */
            beforeEnter: (to) => {
                if (to.query.tab !== 'registrations') return true

                return { name: 'registrations' }
            },
        },
        // The staff module (docs/OSEBJE.md). English paths, as every new one is.
        {
            // „Danes“ — the day in front of the person on duty (docs/OSEBJE.md §11
            // „Danes“, docs/OSEBJE-UI.md). Six sections, one read, and every row a
            // link to the screen that closes it.
            //
            // No permission on the route, like every other screen in this module: the
            // API guards the read with four keys and narrows it to the reader's own
            // departments, and the menu entry carries the same four. A route key here
            // would be a fifth spelling of a rule the server already owns.
            path:      '/staff/today',
            name:      'staff-today',
            component: () => import('@/views/staff/Today.vue'),
            meta:      { requiresAuth: true, module: 'staff' },
        },
        {
            // The rota — who is in the house this week, and whether that is enough.
            path:      '/staff/rota',
            name:      'staff-rota',
            component: () => import('@/views/staff/Rota.vue'),
            meta:      { requiresAuth: true, module: 'staff' },
        },
        {
            // „Jaz“ — the whole personal half of the module on one page: one's own clock,
            // one's own week, one's own leave and one's own documents, as four tabs
            // (docs/OSEBJE-UI.md §3).
            //
            // Self-service: no permission, no selected house — the API narrows every one
            // of these reads to the signed-in account, and that narrowing is the guard.
            // The `staff` module is the only real fence and it is here. It lives in this
            // portal until `staff-web` exists, and is written to be moved there whole —
            // which is now one page rather than four.
            path:      '/staff/me',
            name:      'staff-me',
            component: () => import('@/views/staff/Me.vue'),
            meta:      { requiresAuth: true, module: 'staff' },
        },
        {
            // The old address of Moj urnik. It is in the notice mail about a changed
            // shift (docs/OBVESCANJE.md), so it answers for good and lands on the tab.
            path:     '/staff/my-rota',
            name:     'staff-my-rota',
            redirect: { name: 'staff-me', query: { tab: 'rota' } },
        },
        {
            // The old address of Moja ura, which is the page's first tab.
            path:     '/staff/my-time',
            name:     'staff-my-time',
            redirect: { name: 'staff-me' },
        },
        {
            // Moj dopust as its own address.
            //
            // The service wrote this path into the notice about an annual-leave decision
            // before the personal screens became one page, and it costs one line to keep
            // answering it. `Logic.staffNoticeLink` now sends `/staff/me?tab=vacation`
            // itself — but a letter is a thing that has already been posted, and this is
            // the same insurance `/staff/my-rota` carries for exactly that reason.
            path:     '/staff/my-vacation',
            name:     'staff-my-vacation',
            redirect: { name: 'staff-me', query: { tab: 'vacation' } },
        },
        {
            // The house's own working-time record — days by person, the unfinished ones
            // first (§8). `staff:time` on the API; no key on the route, like every other
            // screen here.
            path:      '/staff/time',
            name:      'staff-time',
            component: () => import('@/views/staff/TimeRecord.vue'),
            meta:      { requiresAuth: true, module: 'staff' },
        },
        {
            // Corrections waiting on a decision (§9). Nobody decides their own, which
            // the API enforces and this screen explains.
            path:      '/staff/time-corrections',
            name:      'staff-time-corrections',
            component: () => import('@/views/staff/TimeCorrections.vue'),
            meta:      { requiresAuth: true, module: 'staff' },
        },
        {
            // Requests for leave, and the days of absence the record holds (§10). Two
            // things on one screen because they are two collections with opposite
            // lifetimes: a refused request may be thrown away like any draft, and a
            // recorded day may not be touched at all.
            path:      '/staff/absences',
            name:      'staff-absences',
            component: () => import('@/views/staff/Absences.vue'),
            meta:      { requiresAuth: true, module: 'staff' },
        },
        {
            // Every person's leave standing for one year, and the decision that settles
            // it (docs/OSEBJE-UI.md §5). `staff:time` reads it — a balance is a reading
            // of the record — and issuing a decision asks for `staff:manage`, which the
            // screen itself gates.
            path:      '/staff/vacation',
            name:      'staff-vacations',
            component: () => import('@/views/staff/Vacations.vue'),
            meta:      { requiresAuth: true, module: 'staff' },
        },
        {
            // The personnel register: dated periods of work, never a person's status.
            path:      '/staff/people',
            name:      'staff-people',
            component: () => import('@/views/staff/People.vue'),
            meta:      { requiresAuth: true, module: 'staff' },
        },
        {
            // ONE person, whole: the contact details from their account, the periods,
            // the leave decisions, the documents and this month of the record
            // (docs/OSEBJE-UI.md §4). Opened from the register — which is the journey it
            // exists for — and a link somebody can send.
            path:      '/staff/people/:userId',
            name:      'staff-person',
            component: () => import('@/views/staff/Person.vue'),
            meta:      { requiresAuth: true, module: 'staff' },
        },
        {
            // The old address of the register.
            path:     '/staff/employments',
            name:     'staff-employments',
            redirect: { name: 'staff-people' },
        },
        {
            // The staff registers — departments and posts (docs/OSEBJE.md §5). They were
            // under Settings, where a house does open them once and leave them alone;
            // they are under Zaposleni → Kadri now, beside the cards that name them,
            // because that is who fills them in (docs/OSEBJE-UI.md §2).
            path:      '/staff/catalogue',
            name:      'staff-catalogue',
            component: () => import('@/views/staff/Catalogue.vue'),
            meta:      { requiresAuth: true, module: 'staff' },
        },
        {
            // The house's own structure, drawn from the registers and the cards in force
            // on a day (docs/OSEBJE.md §17). Beside the register it is derived from,
            // because it is the same two codebooks seen from the other end: the
            // catalogue is the entries, this is what they add up to.
            path:      '/staff/org-chart',
            name:      'staff-org-chart',
            component: () => import('@/views/staff/OrgChart.vue'),
            meta:      { requiresAuth: true, module: 'staff' },
        },
        {
            // Needed hours against rostered ones — the reason this module is in a PMS
            // and not in any system that has never seen a reservation (§11).
            path:      '/staff/coverage',
            name:      'staff-coverage',
            component: () => import('@/views/staff/Coverage.vue'),
            meta:      { requiresAuth: true, module: 'staff' },
        },
        {
            path:      '/finance',
            name:      'finance',
            component: () => import('@/views/finance/Finance.vue'),
            meta:      { requiresAuth: true },
        },
        // Settings is a menu rather than a page: what used to be three cards on one
        // screen — the browser's own look, the accounts, the roles — are three
        // different questions asked by different people at different times.
        {
            // A page and no longer a redirect onto the accounts screen. The gear in the
            // top bar opens it, and what it holds is the same seven groups the sidebar
            // draws under /settings — so somebody who came to change one thing reads a
            // card with that thing's name on it instead of a list of everybody's
            // accounts.
            path:      '/settings',
            name:      'settings',
            component: () => import('@/views/settings/SettingsHub.vue'),
            meta:      { requiresAuth: true },
        },
        {
            path:      '/settings/users',
            name:      'settings-users',
            component: () => import('@/views/settings/Users.vue'),
            meta:      { requiresAuth: true },
        },
        {
            // The roles, granular. Seventy-nine permissions in twenty-five domains
            // outgrew the dialog on the Accounts screen — and what the page adds is not
            // only room but the sentence under each name, which is what somebody
            // building a role actually reads.
            //
            // The role is IN THE ADDRESS so that a link to one is a link somebody can
            // send, and so that a reload stays on the role that was being edited. It is
            // optional: opening the page bare lands on the first role.
            path:      '/settings/roles/:roleId?',
            name:      'settings-roles',
            component: () => import('@/views/settings/Roles.vue'),
            meta:      { requiresAuth: true },
        },
        // Everything about selling over a counter: the counters themselves, how the
        // storeroom is filed, and whether the house can certify a receipt. Three
        // questions that used to be asked on three screens — see Tills.vue.
        {
            // Verige (WP-M5, docs/VERIGE-ZASLONI.md): which houses buy as one.
            //
            // A tenant-level register — the address is `/chains` and no property is
            // named, because a chain is about several houses at once. `meta.module:
            // 'chains'`, like the two buying screens it decides, so a bookmark kept from
            // before the licence lands on „modul ni vključen“.
            path:      '/settings/chains',
            name:      'settings-chains',
            component: () => import('@/views/settings/chains/Chains.vue'),
            meta:      { requiresAuth: true, module: 'chains' },
        },
        {
            path:      '/settings/tills',
            name:      'settings-tills',
            component: () => import('@/views/settings/Tills.vue'),
            meta:      { requiresAuth: true },
        },
        {
            // The rate card the municipality sets and the grounds on which a guest does
            // not pay it (docs/ETURIZEM.md §6). Beside the tills because it is money the
            // house collects on somebody else's behalf, and because the person who fills
            // it in is the one who fills those in.
            //
            // No `module` meta: the tourist tax is not a feature a hotel buys, it is a
            // law it obeys. The permission is the whole gate, and the page draws it —
            // there is no route-level permission guard in this portal.
            path:      '/settings/turisticna-taksa',
            name:      'settings-tourist-tax',
            component: () => import('@/views/settings/TouristTax.vue'),
            meta:      { requiresAuth: true },
        },
        {
            // What the house sets up once about the dining room door: the rooms, the
            // sittings, what the screen may offer, and the tablets themselves
            // (docs/DINING.md).
            path:      '/settings/dining',
            name:      'settings-dining',
            component: () => import('@/views/settings/Dining.vue'),
            meta:      { requiresAuth: true },
        },
        {
            // The machines the housekeepers carry. They used to be enrolled from the
            // dining screen, under a heading that called a telephone a tablet
            // (docs/HOUSEKEEPING-APP.md §2).
            path:      '/settings/housekeeping',
            name:      'settings-housekeeping',
            component: () => import('@/views/settings/Housekeeping.vue'),
            meta:      { requiresAuth: true, module: 'operations' },
        },
        {
            // Where the house posts what it has approved (docs/OBJAVE.md). A
            // credential screen, so property:write guards the reading of it too.
            //
            // Publishing and the house's own website (Property.Site.Published, edited
            // on the property dialog) both belong to `marketing` since 2026-09-03
            // (docs/MODULI.md) — a hotel without the module has neither.
            path:      '/settings/objave',
            name:      'settings-publishing',
            component: () => import('@/views/settings/Publishing.vue'),
            meta:      { requiresAuth: true, module: 'marketing' },
        },
        {
            // The screens the house has hung on its walls (docs/DISPLAY.md). A
            // television enrols itself and waits here to be recognised.
            // Since 1.494.1 it is one area of Settings → Screens; the old address keeps
            // opening it, because a link in a chat or a bookmark on the desk outlives
            // a menu.
            path:     '/settings/sobni-zasloni',
            name:     'settings-display',
            redirect: to => ({ name: 'settings-screens', query: { ...to.query, area: 'rooms' } }),
        },
        {
            // The panels in the PUBLIC parts of the building, and what they loop
            // through (docs/DISPLAY.md §10). Its own page and not a fifth tab of the
            // room screens: that one is about the equipment and the bedrooms, this is
            // about the foyer, and the unit here is a slide with a dwell rather than a
            // screen somebody opens with a remote.
            path:     '/settings/signage',
            name:     'settings-signage',
            // Signage now opens inside the rooms' „Vsebina zaslonov“ tab, and its own
            // tab travels as `signage` — an old `?tab=devices` meant the signage list.
            redirect: to => ({
                name:  'settings-screens',
                query: { ...to.query, area: 'signage', tab: 'content', ...(to.query.tab ? { signage: to.query.tab } : {}) },
            }),
        },
        {
            // Both kinds of screen behind one menu entry (docs/DISPLAY.md §10): the
            // televisions in the rooms and the panels in the public parts. Each keeps
            // its own page and tabs; this route only puts them side by side.
            path:      '/settings/zasloni',
            name:      'settings-screens',
            component: () => import('@/views/settings/Screens.vue'),
            meta:      { requiresAuth: true },
        },
        {
            // What the house spends on models, month by month (docs/AI.md). In the
            // settings and not in the reports because it is read beside where the
            // paid feature is switched on — and by the accountant, who holds the
            // other key to it.
            path:      '/settings/ai-usage',
            name:      'settings-ai-usage',
            component: () => import('@/views/settings/AIUsage.vue'),
            meta:      { requiresAuth: true },
        },
        {
            // Whether the house's assistant answers a GUEST, and what he may prepare for
            // them to confirm (plan §11a). Its own screen and not a block on the property
            // dialog: it switches a PAID feature on for people the hotel does not employ,
            // and it writes one field of its own through its own route.
            //
            // `guest`, the same module the guest portal itself is behind — a house that
            // never bought the portal has nowhere for this to appear.
            path:      '/settings/milo',
            name:      'settings-milo',
            component: () => import('@/views/settings/Milo.vue'),
            meta:      { requiresAuth: true, module: 'guest' },
        },
        {
            // What the house LOOKS like to a guest — the name, the line under it, the
            // colours, the theme, the corners and the monogram (docs/PLAN_GOST.md §A2).
            // The record has existed since the guest portal was built and nothing in any
            // portal wrote it: `PUT /properties/{id}/portal` was reachable only by API,
            // and a house went on greeting its guests under the name of a town it is not
            // in because the desk could not see the line.
            //
            // `guest`, the same module as Milo beside it and as the portal itself: a
            // house that never bought the portal has nothing here to dress.
            // „Mesto v živo“ — whether the assistants may answer how busy the town is,
            // out of which of the four sources, and with whose TomTom key
            // (docs/AI.md §29). Its own screen for the reason Milo's is: it carries a
            // CREDENTIAL, and a credential must not travel through the whole-property
            // save the property dialog makes.
            //
            // NO module gate, unlike the two cards around it. What it configures is read
            // by BOTH assistants — the reception desk's and the guest portal's — so
            // hanging it on `guest` would leave a house that never bought the portal
            // unable to configure a tool its own receptionists are already offered.
            path:      '/settings/mesto-v-zivo',
            name:      'settings-city-pulse',
            component: () => import('@/views/settings/CityPulse.vue'),
            meta:      { requiresAuth: true },
        },
        {
            // Whether a guest is asked for the stay's four-digit PIN before ordering
            // room service or booking a service, on the phone portal and/or the room
            // television, and whether it is e-mailed at check-in. property:write, and
            // no module gate: the television asks for it too, with or without the portal.
            path:      '/settings/pin-za-narocila',
            name:      'settings-order-pin',
            component: () => import('@/views/settings/OrderPin.vue'),
            meta:      { requiresAuth: true },
        },
        {
            path:      '/settings/gostov-portal',
            name:      'settings-guest-portal',
            component: () => import('@/views/settings/GuestPortal.vue'),
            meta:      { requiresAuth: true, module: 'guest' },
        },
        {
            // The house's own AJPES account for the STATE'S business register — who
            // legally represents a company (docs/PODJETJA.md § 10). A credential
            // screen, so property:write guards the reading of it too.
            //
            // Its own page rather than a card on another: it is the one register in
            // this product that COSTS the hotel money, per question, on an account it
            // orders in its own name — and a paid credential buried under a heading
            // about something else is a credential nobody finds when the units run out.
            path:      '/settings/poslovni-register',
            name:      'settings-business-register',
            component: () => import('@/views/settings/BusinessRegister.vue'),
            meta:      { requiresAuth: true },
        },
        {
            // Which guest-room system reads this house, where its equipment is, and
            // which KNX group address is which room's what (docs/KNX.md §4,
            // docs/SOBNA-TELEMETRIJA.md §4.8). A connection screen, so property:write
            // guards the reading of it too — it carries an interface address, a mapping
            // of every room in the building, and the fact that a credential exists.
            path:      '/settings/grms',
            name:      'settings-grms',
            component: () => import('@/views/settings/GRMS.vue'),
            meta:      { requiresAuth: true },
        },
        {
            // The demonstration console (docs/SOBNA-TELEMETRIJA.md §4.7.8b). The route
            // exists for every account; what does not exist for most of them is the
            // MENU entry, because the service answers this screen with an empty
            // scenario list on any house that is not running simulated equipment. The
            // page says so itself when it is reached directly.
            path:      '/settings/demo-pult',
            name:      'settings-demo',
            component: () => import('@/views/settings/DemoConsole.vue'),
            meta:      { requiresAuth: true },
        },
        {
            // The old address of the registers, which have moved into the staff module.
            path:     '/settings/staff-catalogue',
            name:     'settings-staff-catalogue',
            redirect: { name: 'staff-catalogue' },
        },
        {
            // Everything a house decides once about its people, as four tabs: the
            // working-time record and the corridor tablets, the holiday calendar, the
            // leave rules and the document templates (docs/OSEBJE-UI.md §2).
            //
            // The address is the one it always had. This is the SAME page with tabs on
            // it, so there is nothing to redirect — an old link opens the first tab,
            // which is what it opened before.
            path:      '/settings/staff',
            name:      'settings-staff',
            component: () => import('@/views/settings/Staff.vue'),
            meta:      { requiresAuth: true, module: 'staff' },
        },
        {
            // The house's revision trail (docs/REVIZIJSKA_SLED.md): every act of every
            // domain, per person and per event, including everything anybody asked Milo
            // to do.
            //
            // No `module` meta, deliberately, and for the reason Obveščanje beside it has
            // none: what it records is the whole service and not one licensed feature, so
            // a gate here would hide a hotel's own audit trail behind somebody else's
            // module. The permission is the whole gate and the page draws it — there is
            // no route-level permission guard in this portal.
            path:      '/settings/revizijska-sled',
            name:      'settings-audit',
            component: () => import('@/views/settings/AuditTrail.vue'),
            meta:      { requiresAuth: true },
        },
        {
            path:      '/settings/appearance',
            name:      'settings-appearance',
            component: () => import('@/views/settings/Appearance.vue'),
            meta:      { requiresAuth: true },
        },
        {
            // How this house reaches its own people: the mail server it already had,
            // the text-message gateway, the chat applications, and which of the
            // service's own events it sends at all (docs/OBVESCANJE.md). A credential
            // screen, so property:write guards the reading of it too.
            //
            // No `module` meta, deliberately. The transport is house-wide and belongs
            // to no module — the rota is only the first thing to use it — and a gate
            // here would put the settings for a hotel's own SMTP server behind a
            // licence for somebody else's feature.
            path:      '/settings/obvescanje',
            name:      'settings-notifications',
            component: () => import('@/views/settings/Notifications.vue'),
            meta:      { requiresAuth: true },
        },
        {
            // A person's own contacts and the channels they want. Reached by clicking
            // one's own name in the top bar, and by nothing else — it is not in the
            // menu, because it is not a screen anybody navigates TO.
            //
            // No permission at all, like Appearance above it and for the reason
            // docs/OSEBJE.md §13 gives: self-service is the other side of being in the
            // record, not a right somebody grants. It is not under /settings either —
            // Settings is the house, and this is the person.
            path:      '/moj-profil',
            name:      'profile',
            component: () => import('@/views/Profile.vue'),
            meta:      { requiresAuth: true },
        },
    ],
})

/**
 * The screen the FIRST navigation of a tab is on its way to.
 *
 * Until that navigation resolves, `router.currentRoute` is `START_LOCATION` — which is
 * not a screen and never was one: no name, no entry in the menu, and a path of `/` that
 * this portal answers with a redirect. The first navigation is also the slowest of the
 * session, because the guard below awaits the account and the licence, and the shell is
 * drawn and clickable for the whole of that wait.
 *
 * A menu that spends that wait pointing at nothing does damage rather than nothing.
 * „Nazaj v aplikacijo“ wrote `/` down as the screen to come back to and led to „Danes“,
 * and the fold somebody opened while they waited was shut again the moment the screen
 * they had asked for finally landed — because that arrival looked to the sidebar like
 * somebody navigating out of the fold they had just opened.
 *
 * The address bar has named that screen since the first byte, so the menu follows the
 * navigation that is under way instead (`libs/nav.ts`) — and ONLY while the router has
 * no current route of its own. Once it has one, this is never read again: a menu that
 * moved to the next screen before the page did would be a menu that lies about where
 * somebody is.
 *
 * Registered before the guard below, and before `app.use(router)` sets the first
 * navigation off, so it is already written down while those guards are still awaiting.
 */
export const openingRoute = shallowRef<RouteLocationNormalized | null>(null)

router.beforeEach(to => { openingRoute.value = to })

/**
 * Signing in, and the licence.
 *
 * The module half is docs/MODULI.md §3.3: a direct address into a module this hotel
 * has not bought lands on ONE page that says so, rather than on a blank screen or on a
 * 404 that would be a lie — the screen exists, the hotel simply has not bought it.
 *
 * The licence is AWAITED rather than read as it stands. On a reload the guard runs
 * before anything has asked the service, and an unread licence looks exactly like an
 * empty one — so without the await the first navigation of every reload would send
 * somebody to the "not enabled" page and the app would then quietly correct itself.
 * `ensureUserData` is the same read the app shell makes, shared, and it never rejects:
 * a licence that could not be read gates nothing.
 */
router.beforeEach(async (to, from, next) => {
    const appStore = useAppStore()
    const loggedIn = isAuthenticated()

    // Somebody who is signed in has no business on the sign-in page, and where they are
    // sent instead is the landing rule — the same one `/` and Login.vue read. It used to
    // name the chessboard, which since v1.543.0 is neither the landing screen nor a screen
    // every account may open: a housekeeper who pressed „back“ onto /login was thrown at a
    // room plan they are refused.
    if (loggedIn && to.name === 'login') {
        await appStore.ensureUserData()

        const { landingRouteName } = await landingRule()

        return next({ name: landingRouteName() })
    }

    /**
     * The landing rule, asked a second time — and only on the very first navigation of
     * a browser tab.
     *
     * The root address resolves its redirect while the account is still being read
     * (`App.vue` asks on mount), so a cold load at `/` answers „Danes“ for everybody,
     * including the housekeeper whose whole menu is the floor overview. Here the
     * licence and the permissions can be AWAITED, so the rule gets the answer it would
     * have given if it had been able to wait.
     *
     * `START_LOCATION` is what keeps this from hijacking a later visit: somebody who
     * presses „Danes“ in the menu at eleven o'clock is asking for that page, and a guard
     * that sent them somewhere else would be a menu entry that does not work.
     */
    if (loggedIn && from === START_LOCATION && to.name === LANDING_ROUTE) {
        await appStore.ensureUserData()

        const { landingRouteName } = await landingRule()

        const landing = landingRouteName()

        if (landing !== LANDING_ROUTE) return next({ name: landing })
    }

    if (to.meta.requiresAuth && !loggedIn) {
        appStore.setLogout()

        return next({ name: 'login' })
    }

    /**
     * A screen may need ONE licence or several.
     *
     * `meta.module` is the ordinary case and is unchanged. `meta.modules` is for a screen
     * that stands on two of them at once — the transfers between houses of a chain need
     * `materials` (there is no shelf for the goods to leave without it) AND `chains`
     * (there is nowhere for them to go) — and it refuses on the FIRST one the house does
     * not hold, so the page that answers names a licence somebody can actually buy rather
     * than a list.
     */
    const module  = to.meta.module as string | undefined
    const modules = (to.meta.modules as string[] | undefined) ?? (module ? [module] : [])

    if (loggedIn && modules.length) {
        await appStore.ensureUserData()

        const missing = modules.find(key => !appStore.hasModule(key))

        // The module's key travels in the address so the page can name it, and so that
        // the address somebody lands on is still about the thing they asked for.
        if (missing) return next({ name: 'module-off', query: { module: missing } })
    }

    /**
     * The permission, where a screen declares one.
     *
     * Almost no route here does, and that is unchanged: this portal's rule is that the
     * page draws its own gate, because nearly every screen has a half worth reading
     * without the key that writes it. `meta.permission` is for the screens that have no
     * such half — the till is the first — and it works exactly as it does in
     * pms-housekeeping and pms-services: an account without the key is sent to its home
     * screen rather than to a page it can only be told off by.
     *
     * `meta.permissions` is the same gate for a screen whose FIRST reads need more than
     * one key, and every one of them has to be held. The till is the reason it exists:
     * `retail:sell` opens the keypad, but the screen cannot draw itself without reading
     * the tills (`retail:read`) and the shelf behind them (`stock:read`), so an account
     * holding only the first arrived at a page that could do nothing. A route declaring
     * both forms is simply the union — `permission` is one more name on the list.
     *
     * The licence is awaited above; the permissions are read from the same answer, so a
     * reload cannot decide this against an unread account.
     *
     * Where it sends somebody is the LANDING RULE and no longer „Načrt sob“, since
     * 2026-09-20. The chessboard was written down here while it was the screen everybody
     * could open, and it has been neither the landing nor that screen since v1.543.0: a
     * housekeeper who typed the address of a till was thrown at a room plan they are also
     * refused, which is the same defect one screen along. The rule is the one `/`, the
     * sign-in and the first navigation all read, so there is one answer to „where do I
     * belong“ in this portal and not two.
     *
     * Only when it is a DIFFERENT screen. A guard that redirects to a route it would
     * refuse again is a loop, and a loop is worse than a page that says no.
     */
    const permission  = to.meta.permission as string | undefined
    const permissions = [...((to.meta.permissions as string[] | undefined) ?? []), ...(permission ? [permission] : [])]

    if (loggedIn && permissions.length) {
        await appStore.ensureUserData()

        if (permissions.some(key => !appStore.can(key))) {
            const { landingRouteName } = await landingRule()

            const landing = landingRouteName()

            if (landing !== to.name) return next({ name: landing })
        }
    }

    next()
})

/**
 * A screen whose code is no longer on the server.
 *
 * Every screen is loaded when it is first opened. Deploying a new version replaces
 * those files with differently named ones, so a browser tab that was open across a
 * deploy asks for a file that no longer exists — and the click does nothing at all.
 * No error, no message: the person presses "Objekti" and the screen stays where it is.
 *
 * The tab heals itself by loading the new version once, at the address the person was
 * going to. The flag is what keeps that from becoming a loop when the failure is
 * something else — a portal that reloads forever is worse than one that does nothing.
 */
const RELOAD_FLAG = 'pms:reloaded-for-chunk'

const isMissingChunk = (error: unknown): boolean => {
    const message = error instanceof Error ? error.message : String(error)

    return /dynamically imported module|Importing a module script failed|Failed to fetch/i.test(message)
}

router.onError((error, to) => {
    if (!isMissingChunk(error)) return

    if (sessionStorage.getItem(RELOAD_FLAG)) return

    sessionStorage.setItem(RELOAD_FLAG, '1')
    window.location.assign(to.fullPath)
})

// A navigation that worked is the proof the code is current, so the guard is cleared
// and the next deploy gets its one reload too.
router.afterEach(() => sessionStorage.removeItem(RELOAD_FLAG))

export default router
